import _defineProperty from "/home/runner/work/doctocat/doctocat/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/home/runner/work/doctocat/doctocat/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/home/runner/work/doctocat/doctocat/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["children"],
    _excluded2 = ["sx"],
    _excluded3 = ["children", "align"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

import React from 'react';
import { useSSRSafeId } from '@react-aria/ssr';
import { TriangleDownIcon } from '@primer/octicons-react';
import { AnchoredOverlay } from './AnchoredOverlay';
import { useProvidedRefOrCreate, useProvidedStateOrCreate, useMenuInitialFocus, useTypeaheadFocus } from './hooks';
import { Divider } from './ActionList/Divider';
import { ActionListContainerContext } from './ActionList/ActionListContainerContext';
import { Button } from './Button';
import { merge } from './sx';
var MenuContext = /*#__PURE__*/React.createContext({
  renderAnchor: null,
  open: false
});

var Menu = function Menu(_ref) {
  var externalAnchorRef = _ref.anchorRef,
      open = _ref.open,
      onOpenChange = _ref.onOpenChange,
      children = _ref.children;

  var _useProvidedStateOrCr = useProvidedStateOrCreate(open, onOpenChange, false),
      _useProvidedStateOrCr2 = _slicedToArray(_useProvidedStateOrCr, 2),
      combinedOpenState = _useProvidedStateOrCr2[0],
      setCombinedOpenState = _useProvidedStateOrCr2[1];

  var onOpen = React.useCallback(function () {
    return setCombinedOpenState(true);
  }, [setCombinedOpenState]);
  var onClose = React.useCallback(function () {
    return setCombinedOpenState(false);
  }, [setCombinedOpenState]);
  var anchorRef = useProvidedRefOrCreate(externalAnchorRef);
  var anchorId = useSSRSafeId();
  var renderAnchor = null; // 🚨 Hack for good API!
  // we strip out Anchor from children and pass it to AnchoredOverlay to render
  // with additional props for accessibility

  var contents = React.Children.map(children, function (child) {
    if (child.type === MenuButton || child.type === Anchor) {
      renderAnchor = function renderAnchor(anchorProps) {
        return /*#__PURE__*/React.cloneElement(child, anchorProps);
      };

      return null;
    }

    return child;
  });
  return /*#__PURE__*/React.createElement(MenuContext.Provider, {
    value: {
      anchorRef: anchorRef,
      renderAnchor: renderAnchor,
      anchorId: anchorId,
      open: combinedOpenState,
      onOpen: onOpen,
      onClose: onClose
    }
  }, contents);
};

Menu.displayName = "Menu";
var Anchor = /*#__PURE__*/React.forwardRef(function (_ref2, anchorRef) {
  var children = _ref2.children,
      anchorProps = _objectWithoutProperties(_ref2, _excluded);

  return /*#__PURE__*/React.cloneElement(children, _objectSpread(_objectSpread({}, anchorProps), {}, {
    ref: anchorRef
  }));
});
/** this component is syntactical sugar 🍭 */

var MenuButton = /*#__PURE__*/React.forwardRef(function (_ref3, anchorRef) {
  var _ref3$sx = _ref3.sx,
      sxProp = _ref3$sx === void 0 ? {} : _ref3$sx,
      props = _objectWithoutProperties(_ref3, _excluded2);

  return /*#__PURE__*/React.createElement(Anchor, {
    ref: anchorRef
  }, /*#__PURE__*/React.createElement(Button, _extends({
    type: "button",
    trailingIcon: TriangleDownIcon,
    sx: merge({
      // override the margin on caret for optical alignment
      '[data-component=trailingIcon]': {
        marginX: -1
      }
    }, sxProp)
  }, props)));
});

var Overlay = function Overlay(_ref4) {
  var children = _ref4.children,
      _ref4$align = _ref4.align,
      align = _ref4$align === void 0 ? 'start' : _ref4$align,
      overlayProps = _objectWithoutProperties(_ref4, _excluded3);

  // we typecast anchorRef as required instead of optional
  // because we know that we're setting it in context in Menu
  var _React$useContext = React.useContext(MenuContext),
      anchorRef = _React$useContext.anchorRef,
      renderAnchor = _React$useContext.renderAnchor,
      anchorId = _React$useContext.anchorId,
      open = _React$useContext.open,
      onOpen = _React$useContext.onOpen,
      onClose = _React$useContext.onClose;

  var containerRef = /*#__PURE__*/React.createRef();

  var _useMenuInitialFocus = useMenuInitialFocus(open, onOpen, containerRef),
      openWithFocus = _useMenuInitialFocus.openWithFocus;

  useTypeaheadFocus(open, containerRef);
  return /*#__PURE__*/React.createElement(AnchoredOverlay, {
    anchorRef: anchorRef,
    renderAnchor: renderAnchor,
    anchorId: anchorId,
    open: open,
    onOpen: openWithFocus,
    onClose: onClose,
    align: align,
    overlayProps: overlayProps,
    focusZoneSettings: {
      focusOutBehavior: 'wrap'
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: containerRef
  }, /*#__PURE__*/React.createElement(ActionListContainerContext.Provider, {
    value: {
      container: 'ActionMenu',
      listRole: 'menu',
      listLabelledBy: anchorId,
      selectionAttribute: 'aria-checked',
      // Should this be here?
      afterSelect: onClose
    }
  }, children)));
};

Overlay.displayName = "Overlay";
Menu.displayName = 'ActionMenu';
export var ActionMenu = Object.assign(Menu, {
  Button: MenuButton,
  Anchor: Anchor,
  Overlay: Overlay,
  Divider: Divider
});